import { useMemo, useState } from "react";
import { Link } from "wouter";
import { Loader2, ArrowRight, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { AuditLogEntry } from "@workspace/api-client-react";

export type ActivityCategory =
  | "account"
  | "phone"
  | "inventory"
  | "fve"
  | "price"
  | "hold"
  | "sale"
  | "mercury"
  | "banking"
  | "config"
  | "skybox"
  | "other";

const CATEGORY_STYLE: Record<ActivityCategory, string> = {
  account: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  phone: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  inventory: "bg-violet-500/10 text-violet-400 border-violet-500/30",
  fve: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30",
  price: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  hold: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
  sale: "bg-cyan-500/10 text-cyan-400 border-cyan-500/30",
  mercury: "bg-teal-500/10 text-teal-400 border-teal-500/30",
  banking: "bg-indigo-500/10 text-indigo-400 border-indigo-500/30",
  config: "bg-slate-500/10 text-slate-300 border-slate-500/30",
  skybox: "bg-rose-500/10 text-rose-400 border-rose-500/30",
  other: "bg-muted text-muted-foreground border-border",
};

export const CATEGORY_LABEL: Record<ActivityCategory, string> = {
  account: "Account",
  phone: "Phone",
  inventory: "Inventory",
  fve: "FVE",
  price: "Pricing",
  hold: "Holds",
  sale: "Sales",
  mercury: "Mercury",
  banking: "Banking",
  config: "Config",
  skybox: "Skybox",
  other: "Other",
};

interface ActionInfo {
  label: string;
  category: ActivityCategory;
}

const ACTION_META: Record<string, ActionInfo> = {
  "account.update": { label: "Account updated", category: "account" },
  "account.amp_sync": { label: "AMP sync", category: "account" },
  "account.puller_linked": { label: "Puller linked", category: "account" },
  "account.puller_unlinked": { label: "Puller unlinked", category: "account" },
  "account.test_phone": { label: "Phone test sent", category: "phone" },
  "account.bulk_test_phone": { label: "Bulk phone test", category: "phone" },
  "account.phone_health_manual": { label: "Phone health · manual", category: "phone" },
  "account.phone_health_auto": { label: "Phone health · auto", category: "phone" },
  "account.backfill_phone_tags": { label: "Phone tags backfilled", category: "phone" },
  "buyback_account.amp_sync": { label: "Buy-back account refreshed", category: "account" },
  "buyback_account.test_phone": { label: "Buy-back phone test sent", category: "phone" },
  "buyback_account.update": { label: "Buy-back account updated", category: "account" },
  "inventory.note_edited": { label: "Note edited", category: "inventory" },
  "inventory.manual_fields": { label: "Listing fields", category: "inventory" },
  "inventory.listed_on_fve": { label: "Listed on FVE", category: "fve" },
  "inventory.marked_for_fve": { label: "FVE listing removed", category: "fve" },
  "inventory.moved_to_marketplace": { label: "Moved to marketplace", category: "fve" },
  "inventory.buyback_get_in": { label: "Buyback get-in price set", category: "price" },
  "inventory.buyback_sell_confirmed": { label: "Buyback SELL confirmed", category: "fve" },
  "inventory.recommended_price": { label: "Recommended price set", category: "price" },
  "inventory.recommended_price_ack": { label: "Recommended price acknowledged", category: "price" },
  "inventory.email_price_auto_ack": { label: "Recommended price auto-confirmed via email", category: "price" },
  "inventory.email_price_mismatch": { label: "Listed price mismatch (email)", category: "price" },
  "inventory.bulk_hold": { label: "Bulk hold/release", category: "hold" },
  hold: { label: "Hold placed", category: "hold" },
  unhold: { label: "Hold released", category: "hold" },
  "sale.manual_match": { label: "Sale matched", category: "sale" },
  "sale.manual_match_removed": { label: "Sale unmatched", category: "sale" },
  "sale.rematch_applied": { label: "Sale rematch applied", category: "sale" },
  "mercury.micro_deposit": { label: "Micro-deposit", category: "mercury" },
  "mercury.sync": { label: "Mercury sync", category: "mercury" },
  "banking.bank_account.create": { label: "Bank account created", category: "banking" },
  "banking.slot.assign": { label: "Bank slot assigned", category: "banking" },
  "banking.slot.unassign": { label: "Bank slot unassigned", category: "banking" },
  "banking.slot.burn": { label: "Bank slot burned", category: "banking" },
  "banking.account.burn": { label: "Bank account burned", category: "banking" },
  "banking.import": { label: "Banking import", category: "banking" },
  "banking_source.create": { label: "Banking source created", category: "banking" },
  "virtual_bank_account.create": { label: "Virtual bank created", category: "banking" },
  "virtual_bank_account.update": { label: "Virtual bank updated", category: "banking" },
  "event.create": { label: "Event created", category: "config" },
  "event.update": { label: "Event updated", category: "config" },
  "ticketransfer.import": { label: "Ticketransfer task imported", category: "config" },
  "ticketransfer.delete": { label: "Ticketransfer task deleted", category: "config" },
  "event.backfill_performers": { label: "Event performers backfilled", category: "config" },
  "event.backfill_venue_cities": { label: "Event venue cities backfilled", category: "config" },
  "phone_type.update": { label: "Phone type updated", category: "config" },
  "settings.update": { label: "Settings updated", category: "config" },
  "user.update": { label: "User updated", category: "config" },
  "slack.alert_config.update": { label: "Slack alert config", category: "config" },
  "slack.save_mappings": { label: "Slack mappings saved", category: "config" },
  "slack.test_alert": { label: "Slack test alert", category: "config" },
  "skybox.map": { label: "Skybox event mapped", category: "skybox" },
  "skybox.sync": { label: "Skybox sync", category: "skybox" },
  "skybox.alert_config.update": { label: "Skybox alert config", category: "skybox" },
  "skybox.alert_puller": { label: "Skybox puller alert", category: "skybox" },
  "skybox.alert_all": { label: "Skybox bulk alert", category: "skybox" },
};

/** Known actions, in display order, for the filter bar. */
export const KNOWN_ACTIONS: string[] = Object.keys(ACTION_META);

export function actionInfo(action: string): ActionInfo {
  const known = ACTION_META[action];
  if (known) return known;
  // Derive a sensible fallback from the namespace prefix so unmapped actions
  // still get a category color rather than dropping to a bare string.
  const prefix = action.split(".")[0];
  const category = (CATEGORY_LABEL as Record<string, string>)[prefix]
    ? (prefix as ActivityCategory)
    : "other";
  return { label: action, category };
}

export function ActionBadge({ action }: { action: string }) {
  const info = actionInfo(action);
  return (
    <Badge
      variant="outline"
      className={cn("font-mono text-[10px] whitespace-nowrap", CATEGORY_STYLE[info.category])}
      title={action}
    >
      {info.label}
    </Badge>
  );
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "yes" : "no";
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  const s = String(v);
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
}

type Obj = Record<string, unknown>;

function asObj(v: unknown): Obj | null {
  return v && typeof v === "object" && !Array.isArray(v) ? (v as Obj) : null;
}

export function BeforeAfterDiff({
  before,
  after,
  compact = false,
}: {
  before?: unknown;
  after?: unknown;
  compact?: boolean;
}) {
  const b = asObj(before);
  const a = asObj(after);
  const rows = useMemo(() => {
    const keys = new Set<string>([...(b ? Object.keys(b) : []), ...(a ? Object.keys(a) : [])]);
    return [...keys].map((k) => ({
      key: k,
      from: b ? b[k] : undefined,
      to: a ? a[k] : undefined,
      changed: b && a ? JSON.stringify(b[k]) !== JSON.stringify(a[k]) : true,
    }));
  }, [b, a]);

  if (!rows.length) {
    // Fall back to whatever payload exists (e.g. a created entity with no diff).
    const payload = a ?? b;
    if (!payload) return <span className="text-muted-foreground">—</span>;
    return (
      <span className="font-mono text-[11px] text-muted-foreground break-all">
        {formatValue(payload)}
      </span>
    );
  }

  return (
    <div className={cn("flex flex-col gap-0.5", compact && "gap-0")}>
      {rows.map((r) => (
        <div key={r.key} className="flex items-center gap-1.5 text-[11px] leading-tight">
          <span className="font-mono text-muted-foreground">{r.key}</span>
          {b && a ? (
            <span className="inline-flex items-center gap-1 font-mono">
              <span className={cn(r.changed ? "text-red-400/80 line-through" : "text-muted-foreground")}>
                {formatValue(r.from)}
              </span>
              <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
              <span className={cn(r.changed ? "text-emerald-400" : "text-muted-foreground")}>
                {formatValue(r.to)}
              </span>
            </span>
          ) : (
            <span className="font-mono text-foreground/90">{formatValue(r.to ?? r.from)}</span>
          )}
        </div>
      ))}
    </div>
  );
}

function whenLabel(s?: string | null): string {
  if (!s) return "—";
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function countChanges(before: unknown, after: unknown): number {
  const b = asObj(before);
  const a = asObj(after);
  if (!b && !a) return 0;
  const keys = new Set<string>([...(b ? Object.keys(b) : []), ...(a ? Object.keys(a) : [])]);
  if (b && a) {
    return [...keys].filter((k) => JSON.stringify(b[k]) !== JSON.stringify(a[k])).length;
  }
  return keys.size;
}

/** Renders the entity as a deep link: accounts jump to their page, everything
 *  else jumps to a pre-filtered Activity view scoped to that entity. */
function EntityCell({ entry }: { entry: AuditLogEntry }) {
  if (!entry.entityType) return <span className="text-muted-foreground">—</span>;
  const label = `${entry.entityType}${entry.entityId != null ? ` #${entry.entityId}` : ""}`;
  const href =
    entry.entityType === "account" && entry.entityId != null
      ? `/accounts/${entry.entityId}`
      : `/activity?entityType=${encodeURIComponent(entry.entityType)}${entry.entityId != null ? `&entityId=${encodeURIComponent(String(entry.entityId))}` : ""}`;
  return (
    <Link
      href={href}
      className="font-mono text-primary hover:underline whitespace-nowrap"
    >
      {label}
    </Link>
  );
}

/** One audit row. The before/after diff is collapsed by default and expands
 *  inline on demand so dense feeds stay scannable. */
function ActivityRow({ entry, showEntity }: { entry: AuditLogEntry; showEntity?: boolean }) {
  const [open, setOpen] = useState(false);
  const changes = countChanges(entry.before, entry.after);
  const hasPayload = entry.before != null || entry.after != null;
  return (
    <tr className="border-t border-border align-top hover:bg-muted/40">
      <td className="py-2 px-3 text-muted-foreground whitespace-nowrap">{whenLabel(entry.createdAt)}</td>
      <td className="py-2 px-3 break-all">{entry.actorEmail || <span className="text-muted-foreground">system</span>}</td>
      <td className="py-2 px-3"><ActionBadge action={entry.action} /></td>
      {showEntity && (
        <td className="py-2 px-3">
          <EntityCell entry={entry} />
        </td>
      )}
      <td className="py-2 px-3">
        {!hasPayload ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            >
              <ChevronRight className={cn("h-3 w-3 transition-transform", open && "rotate-90")} />
              {open ? "Hide changes" : changes > 0 ? `${changes} change${changes > 1 ? "s" : ""}` : "View details"}
            </button>
            {open && <BeforeAfterDiff before={entry.before} after={entry.after} />}
          </div>
        )}
      </td>
      <td className="py-2 px-3 text-muted-foreground italic break-words">
        {entry.reason || <span className="not-italic">—</span>}
      </td>
    </tr>
  );
}

/** Full activity table used by the Activity page and the per-account tab. */
export function ActivityTable({
  entries,
  isLoading,
  total,
  onLoadMore,
  canLoadMore,
  loadingMore,
  showEntity = true,
  emptyText = "No activity yet.",
}: {
  entries: AuditLogEntry[];
  isLoading?: boolean;
  total?: number;
  onLoadMore?: () => void;
  canLoadMore?: boolean;
  loadingMore?: boolean;
  showEntity?: boolean;
  emptyText?: string;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }
  const colCount = showEntity ? 6 : 5;
  return (
    <div className="space-y-3">
      <div className="border border-border rounded-md overflow-x-auto custom-scrollbar">
        <table className="w-full text-xs min-w-[640px]">
          <thead className="bg-muted text-muted-foreground">
            <tr>
              <th className="text-left py-2 px-3 font-medium w-44">When</th>
              <th className="text-left py-2 px-3 font-medium w-48">Actor</th>
              <th className="text-left py-2 px-3 font-medium w-44">Action</th>
              {showEntity && <th className="text-left py-2 px-3 font-medium w-36">Entity</th>}
              <th className="text-left py-2 px-3 font-medium">Changes</th>
              <th className="text-left py-2 px-3 font-medium w-48">Reason</th>
            </tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <ActivityRow key={e.id} entry={e} showEntity={showEntity} />
            ))}
            {entries.length === 0 && (
              <tr>
                <td colSpan={colCount} className="text-center py-8 text-muted-foreground">
                  {emptyText}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {onLoadMore && (
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs"
            disabled={!canLoadMore || loadingMore}
            onClick={onLoadMore}
          >
            {loadingMore ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : canLoadMore ? "Load more" : "No more"}
          </Button>
          {total != null && (
            <span className="text-[11px] text-muted-foreground tabular-nums">
              Showing {entries.length} of {total}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/** Compact vertical timeline used inside the per-listing detail sheet. */
export function ActivityTimeline({
  entries,
  isLoading,
  emptyText = "No activity yet.",
}: {
  entries: AuditLogEntry[];
  isLoading?: boolean;
  emptyText?: string;
}) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-4 w-4 animate-spin text-primary" />
      </div>
    );
  }
  if (!entries.length) {
    return <p className="text-xs text-muted-foreground py-4 text-center">{emptyText}</p>;
  }
  return (
    <ol className="space-y-2.5">
      {entries.map((e) => (
        <li key={e.id} className="flex flex-col gap-1 border-l-2 border-border pl-3 relative">
          <span className="absolute -left-[5px] top-1 h-2 w-2 rounded-full bg-primary/60" />
          <div className="flex items-center gap-2 flex-wrap">
            <ActionBadge action={e.action} />
            <span className="text-[10px] text-muted-foreground">{whenLabel(e.createdAt)}</span>
            <span className="text-[10px] text-muted-foreground">· {e.actorEmail || "system"}</span>
          </div>
          <BeforeAfterDiff before={e.before} after={e.after} compact />
        </li>
      ))}
    </ol>
  );
}
